import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  Signal,
  TemplateRef,
  ViewChild,
  ViewRef,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ParamMap, Params, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Observable, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, filter, map, skip, startWith, switchMap, takeWhile, tap } from 'rxjs/operators';
import { StatusCodes } from 'http-status-codes';
import {
  ContentType,
  DsButtonVariants,
  DsChipSize,
  DsModalRef,
  DsModalService,
  DsModalVariant,
  Icons,
  TableButtonConfig,
  TableCellData,
  TableCellTextAlign,
  TableChipConfig,
  TableChipTextOrder,
  TableColumnConfig,
  TableRow,
  TableSortEvent,
} from '@levelaccess/design-system';

import { Api, ApiQueryOption } from '@shared/constants/api';
import { $auditFinding } from '@shared/constants/audit-finding';
import { AccessibilityAuditToolNames } from '@shared/constants/audit-tool';
import { AuditTypes } from '@shared/constants/audit-types';
import { $flaw, flawStatus } from '@shared/constants/flaw';
import { $scanIssues } from '@shared/constants/scan-issues';
import { $sortingOrder } from '@shared/constants/sort';
import { $task } from '@shared/constants/task';
import { IAutomatedScanIssue, IScanIssues, IScanIssuesSuccessCriteria } from '@shared/interfaces/scan-issues.interface';
import { ITask } from '@shared/interfaces/task.interface';
import { SharedCollectionsUtility } from '@shared/utils/collections.utility';
import { SharedCommonUtility } from '@shared/utils/common.utility';
import { $advancedSearchForm, IAdvancedSearchRow } from '@client/app/components/advanced-search/advanced-search.component';
import { AdvancedSearchUtil } from '@client/app/components/advanced-search/advanced-search.util';
import { NgbTableComponent } from '@client/app/components/table/ngb-table/ngb-table.component';
import {
  IDclConfig,
  ISelectedSingleData,
  ITableColumn,
  ITableConfig,
  ITableRow,
  SelectAllEvent,
  SortEvent,
} from '@client/app/components/table/ngb-table/utilities/ngb-table.interface';
import { NgbTableUtilities } from '@client/app/components/table/ngb-table/utilities/ngb-table.utilities';
import { $paginationParams } from '@client/app/constants/pagination-params';
import { $viewIssuesParams } from '@client/app/constants/view-issues-params';
import { IHttpErrorResponse } from '@client/app/interfaces/error.interface';
import { IViewIssuesQueryParams } from '@client/app/interfaces/view-issues-query-params.interface';
import { NotificationPosition } from '@client/app/models/notification.model';
import { BusMessageChannels, BusMessageService } from '@client/app/services/bus-message.service';
import { FlawService } from '@client/app/services/flaw.service';
import { LoadErrorHandler } from '@client/app/services/load-error-handler';
import { NotificationService } from '@client/app/services/notification.service';
import { ScanReportService } from '@client/app/services/scan-report.service';
import { TranslateService } from '@client/app/translate/translate.service';
import { ScanService } from '@client/app/services/scan.service';
import { PULLING_INTERVAL } from '@client/app/shared/constants';
import { BrokenLinkData, IScan, SkippedPageData } from '@shared/interfaces/scan.interface';
import { completedStatuses, scanStatus } from '@shared/constants/scanning';
import { $brokenLinkData, $scan } from '@shared/constants/scan';
import { $severity } from '@shared/constants/accessibility';
import { $auditStandardSuccessCriteria } from '@shared/constants/audit-standard-success-criteria';
import { $auditStandard, AuditDbStandard } from '@shared/constants/audit-standard';
import { ICellConfig } from '@client/app/components/table/ngb-table/cells/base-cell/base-cell.component';
import { CommonUtility } from '@client/app/utility/common.utility';
import { FeatureFlagService } from '@client/app/services/feature-flag/feature-flag.service';
import { FeatureFlagCollection } from '@shared/interfaces/feature-flag.interface';
import { FindingTableService } from '@client/app/services/issue-finding-details/finding-table.service';
import { AngularUtility } from '@client/app/utility/angular.utility';
import {
  FindingNavigationDirection,
  IFindingModalNavigation,
  IIssuesViewDetailsModalCloseResult,
} from '@client/app/pages/public/scans/scan-results/issues-view-details-base/issues-view-details-modal.interface';
import {
  createFindingModalNavigation,
  focusIssueViewButton,
  IFindingModalNavigationHandlers,
} from '@client/app/pages/public/scans/scan-results/issues-view-details-base/finding-modal-navigation.utility';
import { AuditTypeToAuditToolName } from '../constants';
import { entriesLimits, IssueTableColumns } from './constants';
import { BannerStatus } from './issues-view-banner/issues-view-banner.component';
import { IssuesViewDetailsModalComponent } from './issues-view-details-modal/issues-view-details-modal.component';

type FindingViewButtonConfig = TableButtonConfig & { id?: string };

interface ISupplementalScanError {
  error: string;
  isRetryable: boolean;
}

type DsTableColumnStyles = NonNullable<TableColumnConfig['styles']>;

interface IDsTableConfig {
  columns: TableColumnConfig[];
  tableCaption: string;
  selectAllCheckboxLabel: string;
  rowCheckboxLabel: string;
}

function isSortEvent(sortEvent: SortEvent | TableSortEvent): sortEvent is SortEvent {
  return 'column' in sortEvent && typeof sortEvent.column === 'string';
}

const tableSortDirectionMap: Record<'asc' | 'desc', $sortingOrder.asc | $sortingOrder.desc> = {
  asc: $sortingOrder.asc,
  desc: $sortingOrder.desc,
};

const dsIssueTableColumnStyles: Partial<Record<IssueTableColumns, DsTableColumnStyles>> = {
  [IssueTableColumns.isNew]: {
    width: '2.5rem',
    paddingLeft: '0',
    paddingRight: '0',
  },
  [IssueTableColumns.findingId]: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    maxWidth: 'max(12.5rem, 20vw)',
  },
  [IssueTableColumns.url]: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    maxWidth: 'max(18.75rem, 20vw)',
    width: 'fit-content',
  },
  [IssueTableColumns.problem]: {
    overflow: 'hidden',
    maxWidth: 'max(25rem, 35vw)',
    minWidth: 'min(12.5rem, 35vw)',
  },
  [IssueTableColumns.activity]: {
    maxWidth: '9.375rem',
    width: '8.125rem',
    paddingRight: '0.95rem',
  },
  [IssueTableColumns.linkedTask]: {
    maxWidth: '9.375rem',
    width: '8.125rem',
    paddingRight: '0.95rem',
  },
};

@Component({
  selector: 'app-issues-view',
  templateUrl: './issues-view.component.html',
  styleUrls: ['./issues-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  animations: [
    trigger('slideInOut', [
      state(
        'true',
        style({
          transform: 'translateY(0%)',
          opacity: 1,
        }),
      ),
      state(
        'false',
        style({
          transform: 'translateY(-100%)',
          opacity: 0,
        }),
      ),
      transition('true <=> false', animate('200ms ease-in-out')),
    ]),
  ],
})
export class IssuesViewComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription;
  private sortOption: SortEvent;
  private selectedIssuesMap: Map<string, IAutomatedScanIssue>;
  private selectedIssueKeySet: Set<string> = new Set<string>();
  private isDigitalAssetFindingsEnabled: boolean = false;
  private scan: IScan;
  private issueDetailsPanelId: string = 'issueDetailsPanel';
  private getAuditReportDetailsSubscription: Subscription;
  private loadedRouteKey: string;

  public advancedFilter: IAdvancedSearchRow[];
  public rowSelectionIndex: number;
  public filterDismissed: boolean | null;

  public ruleId: string;
  public ruleDescription: string;
  public severity: $severity;
  public successCriteriaIdentifier: string[];
  public totalIssues: number;
  public entriesLimits: number[];
  public currentPage: number;
  public scanId: string;
  public requestScanId: string;
  public scanUrl: string;
  public auditType: AuditTypes;
  public isAggregatedView: boolean = false;
  public toolName: AccessibilityAuditToolNames;
  public componentId: string;
  public processingMessage: string;
  public scanRunningChecked: number;
  public scanPointId: string;
  public StatusCodes: typeof StatusCodes;
  public showBanner: boolean;

  @ViewChild('changesNotification')
  public changesNotification: TemplateRef<any>;

  @ViewChild('issuesTable')
  public issueTable: NgbTableComponent;

  public lastChangesCheckedAt: Date;
  public entriesPerPage: number;

  public isFindingScanProcessing: boolean;
  public supplementalScanError: ISupplementalScanError;
  public findingScanId: string;
  public scanRule: IScanIssues;
  public scanIssues: IScanIssues;
  public filteredIssues: IAutomatedScanIssue[];
  public $scanIssues: typeof $scanIssues;
  public $brokenLinkData: typeof $brokenLinkData;
  public tableConfig$: Observable<ITableConfig>;
  public tableData: ITableRow[];
  public dsTableConfig: IDsTableConfig;
  public dsTableData: TableRow[];
  public latestMarkedSelectedIssue: IAutomatedScanIssue;
  public selectedIssues: IAutomatedScanIssue[];
  public selectedIssue: IAutomatedScanIssue;
  public bannerStatus: BannerStatus;

  public taskCreatedSuccessAlert: boolean;
  public taskLink: string;
  public taskProjectId: string;

  public selectionChanged$: Subject<void>;
  public allPageSelected$: Observable<boolean>;
  public selectedIssueKeys$: Observable<string[]>;
  public Icons: typeof Icons = Icons;
  public isDigitalAssetFindingsEnabled$: Observable<boolean>;

  public readonly findingsDetailsRefreshFF: Signal<boolean>;
  public readonly issueTableColumns: typeof IssueTableColumns = IssueTableColumns;

  constructor(
    private activatedRoute: ActivatedRoute,
    private scanReportService: ScanReportService,
    private scanService: ScanService,
    private notificationService: NotificationService,
    private featureFlagService: FeatureFlagService,
    private translateService: TranslateService,
    private busMessageService: BusMessageService,
    private flawService: FlawService,
    private loadErrorHandler: LoadErrorHandler,
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef,
    private location: Location,
    private dsModalService: DsModalService,
    private findingTableService: FindingTableService,
  ) {
    this.StatusCodes = StatusCodes;
    this.isFindingScanProcessing = true;
    this.$brokenLinkData = $brokenLinkData;
    this.$scanIssues = $scanIssues;
    this.subscriptions = new Subscription();
    this.supplementalScanError = null;
    this.loadedRouteKey = null;
    this.scanUrl = null;
    this.requestScanId = null;
    this.scanId = null;
    this.findingScanId = null;
    this.ruleId = null;
    this.ruleDescription = null;
    this.severity = null;
    this.successCriteriaIdentifier = null;
    this.scanRule = null;
    this.totalIssues = null;
    this.componentId = null;
    this.currentPage = 1;
    this.lastChangesCheckedAt = new Date();
    this.selectedIssuesMap = new Map();
    this.selectedIssues = [];
    this.filterDismissed = null;
    this.rowSelectionIndex = -1;
    this.taskCreatedSuccessAlert = false;
    this.processingMessage = '';
    this.scan = null;
    this.scanRunningChecked = 0;
    this.bannerStatus = BannerStatus.default;
    this.showBanner = true;

    this.entriesLimits = entriesLimits;
    this.entriesPerPage = entriesLimits[1];

    this.selectionChanged$ = new Subject<void>();
    this.getAuditReportDetailsSubscription = new Subscription();
    this.dsTableConfig = this.buildDsTableConfig(false);
    this.dsTableData = [];
    this.findingsDetailsRefreshFF = toSignal(
      this.featureFlagService.variation$(FeatureFlagCollection.findingsDetailsRefresh, false).pipe(map(Boolean)),
      { initialValue: false },
    );
  }

  private get issues(): IAutomatedScanIssue[] {
    return this.scanIssues[$scanIssues.issues];
  }

  public get tableSortingOrder(): Record<string, $sortingOrder> {
    return this.sortOption ? { [this.sortOption.column]: this.sortOption.direction } : {};
  }

  public get dsTableCheckboxCellKey(): IssueTableColumns {
    return this.isDigitalAssetFindingsEnabled ? IssueTableColumns.findingId : IssueTableColumns.url;
  }

  private buildDsTableConfig(isDigitalAssetFindingsEnabled: boolean): IDsTableConfig {
    const columns: TableColumnConfig[] = [
      ...(isDigitalAssetFindingsEnabled
        ? [
            {
              key: IssueTableColumns.findingId,
              header: this.translateService.instant('finding_id'),
              contentType: ContentType.chipText,
              sortable: true,
              textAlign: TableCellTextAlign.left,
              styles: dsIssueTableColumnStyles[IssueTableColumns.findingId],
            },
          ]
        : [
            {
              key: IssueTableColumns.isNew,
              header: this.translateService.instant('viewed'),
              contentType: ContentType.chip,
              styles: dsIssueTableColumnStyles[IssueTableColumns.isNew],
            },
          ]),
      {
        key: IssueTableColumns.url,
        header: this.translateService.instant('label_url'),
        contentType: ContentType.link,
        sortable: true,
        textAlign: TableCellTextAlign.left,
        styles: dsIssueTableColumnStyles[IssueTableColumns.url],
      },
      {
        // TODO: replace with ds-table custom cell to render htmlCell / htmlHighlightCell (IBM)
        key: IssueTableColumns.problem,
        header: this.translateService.instant('label_what_the_problem_question'),
        contentType: ContentType.string,
        sortable: true,
        textAlign: TableCellTextAlign.left,
        styles: dsIssueTableColumnStyles[IssueTableColumns.problem],
      },
      {
        // TODO: replace with ds-table custom cell to restore app-view-flaw-status-history when hasHistory is true
        key: IssueTableColumns.activity,
        header: this.translateService.instant(isDigitalAssetFindingsEnabled ? 'label_status' : 'label_activity'),
        contentType: ContentType.chip,
        sortable: true,
        textAlign: TableCellTextAlign.left,
        styles: dsIssueTableColumnStyles[IssueTableColumns.activity],
      },
      {
        key: IssueTableColumns.linkedTask,
        header: this.translateService.instant('task'),
        contentType: ContentType.link,
        sortable: true,
        textAlign: TableCellTextAlign.left,
        styles: dsIssueTableColumnStyles[IssueTableColumns.linkedTask],
      },
      {
        key: IssueTableColumns.viewDetails,
        header: this.translateService.instant('column_header_action'),
        contentType: ContentType.button,
        textAlign: TableCellTextAlign.left,
      },
    ];

    return {
      columns,
      tableCaption: this.translateService.instant('table_caption_issues_details'),
      selectAllCheckboxLabel: this.translateService.instant('select_all_the_issues'),
      rowCheckboxLabel: this.translateService.instant('row_select_checkbox'),
    };
  }

  private mapToDsTableData(): TableRow[] {
    return this.getPageIssues().map((issue: IAutomatedScanIssue, index: number): TableRow => {
      const id: string = issue.rawFindingId.toString();
      const isNewMasterFinding: boolean = this.findingTableService.isNewUnviewedIssue(issue);
      const newMasterFindingCellStyle: Partial<Record<keyof CSSStyleDeclaration, string>> = isNewMasterFinding
        ? { fontWeight: 'bold' }
        : {};
      const viewDetailsButton: FindingViewButtonConfig = {
        label: this.translateService.instant('action_view'),
        variant: DsButtonVariants.microAction,
        microActionIcon: Icons.Eye,
        id: `details-btn-${issue[$flaw._id]}`,
        onClick: (): void => {
          this.selectRow(index);
          this.openIssueDetailsModal(this.selectedIssue);
        },
      };
      const isNewChip: TableChipConfig = this.findingTableService.getViewedChipConfig(isNewMasterFinding);
      const data: Record<string, TableCellData> = {
        [IssueTableColumns.url]: {
          value: issue.url,
          url: issue.url,
          attributes: {
            target: '_blank',
          },
          styles: {
            ...dsIssueTableColumnStyles[IssueTableColumns.url],
            ...newMasterFindingCellStyle,
          },
        },
        [IssueTableColumns.problem]: {
          value: issue.data.actualResult,
          styles: {
            ...dsIssueTableColumnStyles[IssueTableColumns.problem],
            ...newMasterFindingCellStyle,
          },
        },
        [IssueTableColumns.activity]: {
          chip: this.findingTableService.getActivityChipConfig(issue.status),
        },
        [IssueTableColumns.linkedTask]: issue.task
          ? {
              value: issue.task.taskProjectId ?? '',
              rtLink: this.findingTableService.getTaskUrl(issue.task),
              styles: {
                ...dsIssueTableColumnStyles[IssueTableColumns.linkedTask],
                fontWeight: '500',
              },
            }
          : {
              value: '',
              styles: dsIssueTableColumnStyles[IssueTableColumns.linkedTask],
            },
        [IssueTableColumns.viewDetails]: {
          button: viewDetailsButton,
        },
      };

      if (this.isDigitalAssetFindingsEnabled) {
        data[IssueTableColumns.findingId] = {
          chipText: {
            chip: isNewChip,
            text: issue[$flaw.issueId],
            order: TableChipTextOrder.chipText,
          },
          styles: dsIssueTableColumnStyles[IssueTableColumns.findingId],
        };
      } else {
        data[IssueTableColumns.isNew] = {
          chip: isNewChip,
        };
      }

      return {
        key: id,
        data,
      };
    });
  }

  private onUserSwitchedDashboard(): void {
    this.router.navigate(['/', Api.scans], { replaceUrl: true }).then();
  }

  public goBack(): void {
    this.location.back();
  }

  public restartSupplementalScan(): void {
    this.subscriptions.add(
      this.scanService
        .removeScan(this.scan[$scan.workspace].toString(), this.scan[$scan.digitalProperty].toString(), this.scan._id)
        .subscribe(() => {
          this.isFindingScanProcessing = true;
          this.supplementalScanError = null;
          this.scanRunningChecked = 0;
          this.scanId = this.requestScanId;
          this.getAuditReport();
        }),
    );
  }

  private resetReportPollingSubscription(): void {
    this.getAuditReportDetailsSubscription.unsubscribe();
    this.getAuditReportDetailsSubscription = new Subscription();
  }

  private onGetDetailReportError(error: any): void {
    this.loadErrorHandler.handleError('error_while_getting_a_scan_report_detail', error, IssuesViewComponent.name);
  }

  private onGetScanReportSuccess(report: IScanIssues): void {
    this.scanIssues = report;
    this.loadedRouteKey = [this.requestScanId, this.ruleId, this.componentId, this.scanPointId].join('|');
    this.selectedIssues = [];
    this.selectedIssue = null;
    this.rowSelectionIndex = -1;
    this.selectedIssuesMap.clear();
    this.onQueryParamsChange(this.activatedRoute.snapshot.queryParams as IViewIssuesQueryParams);
  }

  private detectChanges(): void {
    if ((this.changeDetectorRef as ViewRef).destroyed) {
      return;
    }
    this.changeDetectorRef.detectChanges();
  }

  private showSupplementalScanCompleteNotification(): void {
    if (this.scanRunningChecked > 1) {
      if (this.supplementalScanError) {
        this.notificationService.error(
          this.translateService.instant('supplemental_scan_error_notification'),
          NotificationPosition.Toast,
        );
      } else {
        this.notificationService.success(
          this.translateService.instant('supplemental_scan_complete_notification'),
          NotificationPosition.Toast,
        );
      }
    }
  }

  private showSupplementalScanRunningNotification(): void {
    if (this.scanRunningChecked === 1 && this.isFindingScanProcessing) {
      this.notificationService.warn(
        this.translateService.instant('supplemental_scan_running_notification'),
        NotificationPosition.Toast,
      );
    }
  }

  private updateProcessingMessage(): void {
    if (this.scanRunningChecked === 0 && this.scanId === this.requestScanId) {
      this.processingMessage = this.translateService.instant('wait_while_retrieve_findings');
    } else {
      this.processingMessage = this.translateService.instant('scan_to_retrieve_findings_info');
    }
    this.changeDetectorRef.detectChanges();
  }

  private getAuditReport(): void {
    this.resetReportPollingSubscription();
    if (this.isFindingScanProcessing) {
      // Get supplemental scan id -> keep pulling supplemental scan status -> if completed, pull issues
      this.getAuditReportDetailsSubscription.add(
        this.scanService
          .getOrCreateSupplementalScan(this.scanId, this.scanUrl)
          .pipe(
            switchMap((findingScanId: string): Observable<IScanIssues> => {
              this.scanId = findingScanId;
              this.updateProcessingMessage();
              return timer(0, PULLING_INTERVAL.FAST)
                .pipe(
                  takeWhile((): boolean => {
                    return this.isFindingScanProcessing;
                  }),
                  switchMap((): Observable<IScan> => {
                    return this.scanService.getScan(findingScanId);
                  }),
                )
                .pipe(
                  tap((scan: IScan): IScan => {
                    this.isFindingScanProcessing = !completedStatuses.includes(scan[$scan.status]);

                    if (this.requestScanId !== scan[$scan._id] && scan[$scan.status] === scanStatus.completed_with_errors) {
                      this.supplementalScanError = this.getSupplementalScanError(scan);
                    }

                    return scan;
                  }),
                  filter((): boolean => {
                    this.scanRunningChecked++;
                    this.showSupplementalScanRunningNotification();
                    return !this.isFindingScanProcessing;
                  }),
                  switchMap((scan: IScan): Observable<IScanIssues> => {
                    this.showSupplementalScanCompleteNotification();
                    this.scan = scan;
                    return this.scanReportService.getIssues(
                      scan._id,
                      this.toolName,
                      this.ruleId,
                      this.scanUrl,
                      this.componentId,
                      this.scanPointId,
                    );
                  }),
                );
            }),
          )
          .subscribe({
            next: this.onGetScanReportSuccess.bind(this),
            error: this.onGetDetailReportError.bind(this),
          }),
      );
    } else {
      this.getAuditReportDetailsSubscription.add(
        this.scanReportService
          .getIssues(this.scanId, this.toolName, this.ruleId, this.scanUrl, this.componentId, this.scanPointId)
          .subscribe({
            next: this.onGetScanReportSuccess.bind(this),
            error: this.onGetDetailReportError.bind(this),
          }),
      );
    }
  }

  private getSupplementalScanError(scan: IScan): ISupplementalScanError {
    const brokenLink: BrokenLinkData = scan[$scan.brokenLinks]?.find(
      (_brokenLink: BrokenLinkData) => _brokenLink[$brokenLinkData.url] === scan[$scan.url],
    );
    const skippedPage: SkippedPageData = scan[$scan.skippedPages]?.find(
      (_skippedPage: SkippedPageData) => _skippedPage[$brokenLinkData.url] === scan[$scan.url],
    );

    if (SharedCommonUtility.notNullish(skippedPage)) {
      return {
        error: this.getSkippedPageErrorMessage(skippedPage.error),
        isRetryable: true,
      };
    }

    if (SharedCommonUtility.isNullish(brokenLink)) {
      return {
        error: this.getScanErrorMessage(),
        isRetryable: true,
      };
    }

    return {
      error: this.getScanErrorMessage(brokenLink[$brokenLinkData.responseCode]),
      isRetryable: this.isRetryableError(brokenLink[$brokenLinkData.responseCode]),
    };
  }

  private getSkippedPageErrorMessage(skippedPageError: string): string {
    return this.translateService.instant('no_scan_results_available_for_findings_details_subtitle_with_error', skippedPageError);
  }

  private getScanErrorMessage(statusCode: number = 0): string {
    if (statusCode === StatusCodes.NOT_FOUND) {
      return this.translateService.instant('supplemental_scan_page_not_found');
    }

    return this.translateService.instant('no_scan_results_available_for_findings_details_subtitle');
  }

  private isRetryableError(statusCode: number = 0): boolean {
    return statusCode !== StatusCodes.NOT_FOUND;
  }

  private getFilteredIssues(): IAutomatedScanIssue[] {
    const defaultSort = (a: IAutomatedScanIssue, b: IAutomatedScanIssue): number => {
      return a.rawFindingId.localeCompare(b.rawFindingId);
    };
    const filterByIgnoredAndUrl = (finding: IAutomatedScanIssue): boolean => {
      const includeByDismissed: boolean =
        this.filterDismissed === null ? true : this.filterDismissed === (finding[$flaw.status] === flawStatus.dismissed);

      const confirmsFilters: boolean = AdvancedSearchUtil.conformsFilters(finding, this.advancedFilter, {
        [$scanIssues.url]: $scanIssues.url,
        [$auditFinding.element]: 'data.element',
        [$auditFinding.elementReference]: 'data.elementReference',
        [$scanIssues.title]: $scanIssues.title,
      });

      return confirmsFilters && includeByDismissed;
    };
    return this.issues.filter(filterByIgnoredAndUrl).sort(defaultSort);
  }

  private getPageIssues(): IAutomatedScanIssue[] {
    return this.filteredIssues.slice(this.getPaginationFromIndex(), this.getPaginationToIndex());
  }

  private getTableData(): ITableRow[] {
    const areDigitalAssetsFindingsEnabled: boolean = this.featureFlagService.variation(
      FeatureFlagCollection.digitalAssetFindings,
      false,
    );

    return this.getPageIssues().map((issue: IAutomatedScanIssue, index: number): ITableRow => {
      const id: string = issue.rawFindingId.toString();
      const isNewMasterFinding: boolean = this.findingTableService.isNewUnviewedIssue(issue);
      const viewedChip: TableChipConfig = this.findingTableService.getViewedChipConfig(isNewMasterFinding);
      const newMasterFindingCellStyle: Partial<Record<keyof CSSStyleDeclaration, string>> = isNewMasterFinding
        ? { fontWeight: 'bold' }
        : {};

      return {
        originalData: issue,
        isHighlighted: isNewMasterFinding,
        data: {
          [IssueTableColumns.checkbox]: NgbTableUtilities.checkboxCell({
            checked: this.selectedIssuesMap.has(id),
            attributes: {
              'aria-label': this.translateService.instant('row_select_checkbox'),
            },
            styles: {
              marginTop: '5px',
            },
            listeners: {
              click: ($event: MouseEvent): void => {
                $event.stopPropagation();
                if (this.selectedIssuesMap.has(id)) {
                  this.selectedIssuesMap.delete(id);
                } else {
                  this.selectedIssuesMap.set(id, issue);
                }
                this.selectedIssues = Array.from(this.selectedIssuesMap.values());
                this.selectionChanged$.next();
                this.updateBannerStatus(BannerStatus.default);
              },
            },
          }),
          [IssueTableColumns.isNew]: NgbTableUtilities.DSChipCell({
            text: viewedChip.text,
            chipColorVariant: viewedChip.colorVariant,
            chipSize: DsChipSize.Small,
          }),
          [IssueTableColumns.findingId]: areDigitalAssetsFindingsEnabled
            ? NgbTableUtilities.textCell({
                text: issue[$flaw.issueId],
              })
            : undefined,
          [IssueTableColumns.url]: NgbTableUtilities.externalLinkCell({
            text: issue.url,
            attributes: {
              href: issue.url,
            },
            styles: {
              ...newMasterFindingCellStyle,
            },
          }),
          [IssueTableColumns.problem]: this.getProblemCell(issue, {
            ...newMasterFindingCellStyle,
          }),
          [IssueTableColumns.activity]: NgbTableUtilities.issueStateLabelCell({
            hasHistory: this.scanReportService.hasHistory(issue),
            flaw: issue,
            status: issue.status,
            tabIndex: -1,
          }),
          [IssueTableColumns.linkedTask]: this.getLinkedTaskCell(issue, (task: Partial<ITask>) =>
            this.findingTableService.getTaskUrl(task),
          ),
          [IssueTableColumns.viewDetails]: NgbTableUtilities.buttonCell({
            text: this.translateService.instant('action_view'),
            classes: ['btn-outline-primary', 'details-btn'],
            listeners: {
              click: (): void => {
                this.selectRow(index);
                if (this.findingsDetailsRefreshFF()) {
                  this.openIssueDetailsModal(this.selectedIssue);
                } else {
                  CommonUtility.setFocusToElement(this.issueDetailsPanelId);
                  CommonUtility.scrollElementIntoView(this.issueDetailsPanelId);
                }
              },
            },
            attributes: { id: `details-btn-${issue[$flaw._id]}` },
          }),
        },
      };
    });
  }

  private getLinkedTaskCell(issue: IAutomatedScanIssue, getTaskUrl: (task: Partial<ITask>) => string): IDclConfig<ICellConfig> {
    if (issue.task) {
      return NgbTableUtilities.linkCell({
        icon: {
          iconName: 'link-intact',
          iconWidth: '12px',
          iconHeight: '12px',
          classes: ['task-link-icon'],
        },
        text: issue.task.taskProjectId ?? '',
        attributes: {
          routerLink: getTaskUrl(issue.task),
        },
        styles: {
          fontWeight: '500',
        },
      });
    }
    return NgbTableUtilities.textCell({ text: '' });
  }

  private getProblemCell(
    issue: IAutomatedScanIssue,
    newMasterFindingCellStyle: Partial<Record<keyof CSSStyleDeclaration, string>>,
  ): IDclConfig<ICellConfig> {
    const cellData: ICellConfig = {
      text: issue.data.actualResult,
      styles: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        ...newMasterFindingCellStyle,
      },
    };

    if (this.toolName === AccessibilityAuditToolNames.ibm) {
      return NgbTableUtilities.htmlHighlightCell(cellData);
    }

    return NgbTableUtilities.htmlCell(cellData);
  }

  private getPaginationFromIndex(): number {
    return (this.currentPage - 1) * this.entriesPerPage;
  }

  private getPaginationToIndex(): number {
    return this.getPaginationFromIndex() + this.entriesPerPage;
  }

  private getTableConfig(isDigitalAssetFindingsEnabled: boolean): ITableConfig {
    const columns: Record<string, ITableColumn> = {
      [IssueTableColumns.checkbox]: {
        translationKey: 'select_all_the_issues',
        selectAllEnabled: true,
        hideLabel: true,
        styles: {
          maxWidth: '3rem',
          width: '2.8rem',
          paddingRight: '0',
          paddingLeft: '0.95rem',
        },
      },
      [IssueTableColumns.isNew]: {
        translationKey: 'viewed',
        styles: {
          width: '40px',
          paddingLeft: '0.95rem',
          paddingRight: '0rem',
          textAlign: 'right',
        },
      },
      [IssueTableColumns.findingId]: {
        translationKey: 'finding_id',
        sortingEnabled: true,
        styles: {
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          maxWidth: 'max(200px, 20vw)',
        },
      },
      [IssueTableColumns.url]: {
        translationKey: 'label_url',
        sortingEnabled: true,
        styles: {
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          maxWidth: 'max(300px, 20vw)',
          width: 'fit-content',
        },
      },
      [IssueTableColumns.problem]: {
        translationKey: 'label_what_the_problem_question',
        sortingEnabled: true,
        styles: {
          overflow: 'hidden',
          maxWidth: 'max(400px, 35vw)',
          minWidth: 'min(200px, 35vw)',
        },
      },
      [IssueTableColumns.activity]: {
        translationKey: isDigitalAssetFindingsEnabled ? 'label_status' : 'label_activity',
        sortingEnabled: true,
        styles: {
          maxWidth: '150px',
          width: '130px',
          paddingRight: '0.95rem',
        },
      },
      [IssueTableColumns.linkedTask]: {
        translationKey: 'task',
        sortingEnabled: true,
        styles: {
          maxWidth: '150px',
          width: '130px',
          paddingRight: '0.95rem',
        },
      },
      [IssueTableColumns.viewDetails]: {
        translationKey: 'column_header_action',
      },
    };

    if (!isDigitalAssetFindingsEnabled) {
      delete columns[IssueTableColumns.findingId];
    }

    return {
      columns: columns,
      caption: this.translateService.instant('table_caption_issues_details'),
      singleSelect: {
        selectable: true,
        selectFirstRow: false,
      },
      emptyState: {
        iconId: 'magnifying-glass-2',
        title: this.translateService.instant('no_search_results_available'),
        subtitle: this.translateService.instant('label_no_search_results_hint'),
      },
    };
  }

  private sort(): void {
    const sortFn = (key: IssueTableColumns, a: IAutomatedScanIssue, b: IAutomatedScanIssue): number => {
      let valA: string = '';
      let valB: string = '';

      switch (key) {
        case IssueTableColumns.url:
          valA = a[$scanIssues.url];
          valB = b[$scanIssues.url];
          break;
        case IssueTableColumns.activity:
          valA = a[$flaw.status];
          valB = b[$flaw.status];
          break;
        case IssueTableColumns.problem:
          valA = a[$scanIssues.problem] ?? a.data?.actualResult ?? '';
          valB = b[$scanIssues.problem] ?? b.data?.actualResult ?? '';
          break;
        case IssueTableColumns.findingId:
          valA = a[$flaw.issueId];
          valB = b[$flaw.issueId];
          break;
        case IssueTableColumns.linkedTask:
          valA = a?.task?.taskProjectId ?? '';
          valB = b?.task?.taskProjectId ?? '';
          break;
        // no default
      }
      return this.sortOption.direction === $sortingOrder.asc ? valA.localeCompare(valB) : valB.localeCompare(valA);
    };

    if (this.sortOption.direction === $sortingOrder.all || SharedCommonUtility.isNullish(this.sortOption.column)) {
      this.filteredIssues = this.getFilteredIssues();
    } else {
      this.filteredIssues.sort(sortFn.bind(this, this.sortOption.column));
    }
  }

  private resetPageSelection(): void {
    this.currentPage = 1;
    this.rowSelectionIndex = -1;
    this.selectedIssue = null;
    this.selectedIssuesMap.clear();
  }

  private reloadParams(): void {
    const queryParams: Params = {
      [$paginationParams.pageNumber]: this.currentPage,
      [$paginationParams.pageSize]: this.entriesPerPage,
      [$paginationParams.sortBy]: this.sortOption?.column ?? '',
      [$paginationParams.direction]: this.sortOption?.direction ?? '',
      [$viewIssuesParams.issueId]: this.selectedIssue?.rawFindingId,
      [$viewIssuesParams.issueKey]: this.selectedIssue?.issueId, // readable ID used with the Public API (e.g. MRV-12345)
      [$viewIssuesParams.dismissedFilter]: this.filterDismissed ?? '',
      [$viewIssuesParams.advancedSearch]: '',
    };

    const hasValue = (advancedSearchRow: IAdvancedSearchRow): boolean =>
      advancedSearchRow[$advancedSearchForm.value]?.trim()?.length > 0;
    if (this.advancedFilter && this.advancedFilter.length > 0 && this.advancedFilter.some(hasValue)) {
      queryParams[$viewIssuesParams.advancedSearch] = btoa(JSON.stringify(this.advancedFilter));
    }

    this.router
      .navigate([], {
        queryParams: queryParams,
        queryParamsHandling: 'merge',
        replaceUrl: true,
      })
      .then();
    this.selectionChanged$.next();
  }

  private setPageFromSelectedIssue(issueId: string): void {
    for (let i = 0; i < SharedCollectionsUtility.chunk(this.filteredIssues, this.entriesPerPage).length; i += 1) {
      const page: IAutomatedScanIssue[] = SharedCollectionsUtility.chunk(this.filteredIssues, this.entriesPerPage)[i];

      for (let j = 0; j < page.length; j += 1) {
        if (page[j].rawFindingId === issueId || page[j].issueId === issueId) {
          this.currentPage = i + 1;
          this.selectedIssue = page[j];
          this.rowSelectionIndex = j;
          return;
        }
      }
    }

    this.notificationService.warn(
      this.translateService.instant('label_issue_not_found_for_selected_filter'),
      NotificationPosition.Toast,
      true,
    );

    this.resetPageSelection();
  }

  private updateTableData(): void {
    this.filteredIssues = this.getFilteredIssues();
    if (this.sortOption) {
      this.sort();
    }

    this.tableData = this.getTableData();
    if (this.findingsDetailsRefreshFF()) {
      this.dsTableData = this.mapToDsTableData();
    }

    this.detectChanges();
  }

  private onQueryParamsChange(params: IViewIssuesQueryParams): void {
    if (SharedCommonUtility.isNullish(this.scanIssues)) {
      return;
    }

    if (params[$paginationParams.pageNumber]) {
      this.currentPage = Number(params[$paginationParams.pageNumber]);
    }

    if (params[$paginationParams.pageSize]) {
      this.entriesPerPage = Number(params[$paginationParams.pageSize]);
    }

    if (params[$viewIssuesParams.advancedSearch]) {
      this.advancedFilter = JSON.parse(atob(params[$viewIssuesParams.advancedSearch]));
    } else {
      this.advancedFilter = [];
    }

    const dismissedValue: string | undefined = params[$viewIssuesParams.dismissedFilter];

    if (dismissedValue) {
      if (dismissedValue === 'true') {
        this.filterDismissed = true;
      } else {
        this.filterDismissed = dismissedValue === 'false' ? false : null;
      }
    }

    this.filteredIssues = this.getFilteredIssues();

    if (params[$paginationParams.sortBy] && params[$paginationParams.direction]) {
      this.sortOption = { column: params[$paginationParams.sortBy], direction: params[$paginationParams.direction] };
      this.sort();
    }

    // snapshot.paramMap is updated atomically when navigation commits, so it always reflects
    // the destination params even if this observable fires before paramMap. loadedRouteKey is
    // set in onGetScanReportSuccess, so this guard is false during any navigation that changes
    // the fetched data until the new data arrives.
    if (params[$viewIssuesParams.issueId]) {
      const snapshot: ParamMap = this.activatedRoute.snapshot.paramMap;
      const currentRouteKey: string = [
        snapshot.get(Api.scanId),
        snapshot.get(Api.ruleId),
        snapshot.get(Api.componentId),
        snapshot.get(Api.pageId),
      ].join('|');
      if (currentRouteKey === this.loadedRouteKey) {
        this.setPageFromSelectedIssue(params[$viewIssuesParams.issueId]);
      }
    }

    this.tableData = this.getTableData();
    if (this.findingsDetailsRefreshFF()) {
      this.dsTableData = this.mapToDsTableData();
    }

    this.detectChanges();
  }

  private subscribeToDigitalPropertySwitch(): void {
    this.subscriptions.add(
      this.busMessageService
        .from(BusMessageChannels.userSwitchedToDigitalProperty)
        .pipe(skip(1))
        .subscribe({ next: this.onUserSwitchedDashboard.bind(this) }),
    );
  }

  private selectRow(rowIndex: number): void {
    this.selectedIssue = this.filteredIssues.slice(this.getPaginationFromIndex(), this.getPaginationToIndex())[rowIndex];
    this.reloadParams();
    if (
      this.selectedIssue &&
      this.findingTableService.isNewUnviewedIssue(this.selectedIssue) &&
      (SharedCommonUtility.isNullish(this.latestMarkedSelectedIssue) ||
        this.selectedIssue._id !== this.latestMarkedSelectedIssue._id)
    ) {
      this.latestMarkedSelectedIssue = this.selectedIssue;
      this.subscriptions.add(this.markFlawAsViewed(this.selectedIssue).subscribe());
    }
  }

  private getCurrentFindingGlobalIndex(): number {
    if (SharedCommonUtility.isNullish(this.selectedIssue)) {
      return -1;
    }

    return this.filteredIssues.findIndex(
      (issue: IAutomatedScanIssue): boolean => issue.rawFindingId === this.selectedIssue.rawFindingId,
    );
  }

  private selectFindingAtGlobalIndex(index: number): void {
    const issue: IAutomatedScanIssue | undefined = this.filteredIssues[index];
    if (SharedCommonUtility.isNullish(issue)) {
      return;
    }

    this.selectedIssue = issue;
    this.currentPage = Math.floor(index / this.entriesPerPage) + 1;
    this.rowSelectionIndex = index % this.entriesPerPage;
    this.reloadParams();

    if (
      this.findingTableService.isNewUnviewedIssue(issue) &&
      (SharedCommonUtility.isNullish(this.latestMarkedSelectedIssue) || issue._id !== this.latestMarkedSelectedIssue._id)
    ) {
      this.latestMarkedSelectedIssue = issue;
      this.subscriptions.add(this.markFlawAsViewed(issue).subscribe());
    }

    if (this.findingsDetailsRefreshFF()) {
      this.dsTableData = this.mapToDsTableData();
      this.detectChanges();
    }
  }

  private getFindingModalNavigationHandlers(): IFindingModalNavigationHandlers {
    return {
      getIssues: (): IAutomatedScanIssue[] => this.filteredIssues,
      getCurrentIndex: (): number => this.getCurrentFindingGlobalIndex(),
      selectAtIndex: (index: number): void => this.selectFindingAtGlobalIndex(index),
      getSelectedIssue: (): IAutomatedScanIssue => this.selectedIssue,
      updateModalFinding: (modalRef: DsModalRef, issue: IAutomatedScanIssue, direction: FindingNavigationDirection): void => {
        modalRef.componentInstance.updateFinding(issue, direction);
      },
    };
  }

  private createFindingModalNavigation(modalRef: DsModalRef): IFindingModalNavigation {
    return createFindingModalNavigation(modalRef, this.getFindingModalNavigationHandlers());
  }

  private focusSelectedIssueViewButton(): void {
    focusIssueViewButton(this.selectedIssue, this.rowSelectionIndex);
  }

  private openIssueDetailsModal(issue: IAutomatedScanIssue): void {
    const isDigitalAssetFindingsEnabled: boolean = this.featureFlagService.variation(
      FeatureFlagCollection.digitalAssetFindings,
      false,
    );
    const modalRef: DsModalRef = this.dsModalService.open(IssuesViewDetailsModalComponent, DsModalVariant.fullPage);
    if (SharedCommonUtility.isNullish(modalRef)) {
      return;
    }

    modalRef.componentInstance.issue = issue;
    modalRef.componentInstance.ruleDetails = this.scanIssues;
    modalRef.componentInstance.scanId = this.scanId;
    modalRef.componentInstance.toolName = this.toolName;
    modalRef.componentInstance.isDigitalAssetFindingsEnabled = isDigitalAssetFindingsEnabled;
    modalRef.componentInstance.auditType = this.auditType;
    modalRef.componentInstance.scanUrl = this.scanUrl;
    modalRef.componentInstance.navigation = this.createFindingModalNavigation(modalRef);

    modalRef.result.then(
      (result: IIssuesViewDetailsModalCloseResult): void => {
        if (result?.reloadNeeded) {
          this.reloadReport({ task: result.task, clearNotifications: false });
        }
        this.focusSelectedIssueViewButton();
      },
      (): void => {
        this.focusSelectedIssueViewButton();
      },
    );
  }

  public get getSelectedIssueBtnSelector(): string {
    return `#details-btn-${this.selectedIssue[$flaw._id]}`;
  }

  public reloadReport(event?: { task?: ITask; clearNotifications?: boolean }): void {
    const { task, clearNotifications } = event;
    if (!SharedCommonUtility.isNullish(task)) {
      this.taskCreatedSuccessAlert = true;
      this.taskProjectId = task[$task.taskProjectId];
      this.taskLink = `/${Api.projects}/${task[$task.projectId]}/${Api.tasks}/${task[$task._id]}`;
      this.bannerStatus = BannerStatus.taskCreated;
    }
    if (clearNotifications) {
      this.notificationService.clear();
    }
    this.getAuditReport();
    this.onSelectAll({ selected: false });
  }

  public updateBannerStatus(status: BannerStatus): void {
    this.showBanner = true;
    this.bannerStatus = status;
  }

  public getPaginationLabel(page: number, entriesPerPage: number, total: number): string {
    return this.translateService.instant('n_of_t', [
      `${Math.min((page - 1) * entriesPerPage + 1, total)} - ${Math.min(page * entriesPerPage, total)}`,
      String(total),
    ]);
  }

  public reloadTableRows(): void {
    if (this.findingsDetailsRefreshFF()) {
      this.dsTableData = this.mapToDsTableData();
      this.detectChanges();
      return;
    }

    this.tableData = this.getTableData();
    this.issueTable?.reloadWithNewData(this.tableData);
  }

  public markFlawAsViewed(issue: IAutomatedScanIssue): Observable<any> {
    return this.flawService.setViewed(issue._id).pipe(
      tap(() => {
        issue.viewed = true;
        this.reloadTableRows();
      }),
      catchError((e: IHttpErrorResponse): Observable<any> => of(console.error('markFlawAsViewed error', e, issue[$flaw._id]))),
    );
  }
  public onAdvancedSearch(searchRows: IAdvancedSearchRow[]): void {
    this.advancedFilter = searchRows;
    this.resetPageSelection();
    this.reloadParams();
  }

  public onPageChange(page: number): void {
    this.currentPage = page;
    this.rowSelectionIndex = -1;
    this.selectedIssue = null;
    this.reloadParams();
  }

  public onEntriesPerPageChange(entriesPerPage: number): void {
    this.entriesPerPage = entriesPerPage;
    this.resetPageSelection();
    this.reloadParams();
  }

  public onRowSelected($event: ISelectedSingleData): void {
    if (SharedCommonUtility.notNullish($event)) {
      this.selectRow($event.index);
      CommonUtility.setFocusToElement(this.issueDetailsPanelId);
      CommonUtility.scrollElementIntoView(this.issueDetailsPanelId);
    }
  }

  public onFilterByActivity($event: boolean | null): void {
    this.filterDismissed = $event;
    this.currentPage = 1;
    this.rowSelectionIndex = -1;
    this.selectedIssuesMap.clear();
    this.selectedIssue = null;
    this.reloadParams();
  }

  public onSelectAll(event: SelectAllEvent): void {
    this.getPageIssues().forEach((issue: IAutomatedScanIssue) => {
      const id: string = issue.rawFindingId;
      if (event.selected) {
        this.selectedIssuesMap.set(id, issue);
      } else {
        this.selectedIssuesMap.delete(id);
      }
    });
    this.selectedIssues = Array.from(this.selectedIssuesMap.values());
    this.selectionChanged$.next();
    if (this.selectedIssues.length !== 0) {
      this.bannerStatus = BannerStatus.default;
    }
    if (!this.findingsDetailsRefreshFF()) {
      this.updateTableData();
    }
  }

  public onDsTableSelectedRowKeysChange(selectedRowKeys: string[]): void {
    const selectedRowKeysSet: Set<string> = new Set<string>(selectedRowKeys);

    for (const issue of this.getPageIssues()) {
      const id: string = issue.rawFindingId.toString();
      const isSelected: boolean = selectedRowKeysSet.has(id);
      const wasSelected: boolean = this.selectedIssueKeySet.has(id);

      if (isSelected && !wasSelected) {
        this.selectedIssuesMap.set(id, issue);
      } else if (!isSelected && wasSelected) {
        this.selectedIssuesMap.delete(id);
      }
    }

    this.selectedIssueKeySet = selectedRowKeysSet;
    this.selectedIssues = Array.from(this.selectedIssuesMap.values());
    this.selectionChanged$.next();
    if (this.selectedIssues.length !== 0) {
      this.bannerStatus = BannerStatus.default;
    }
  }

  public onSort(sort: SortEvent): void {
    this.sortOption = sort;
    this.resetPageSelection();
    this.reloadParams();
  }

  public applySort(sortEvent: SortEvent | TableSortEvent): void {
    if (isSortEvent(sortEvent)) {
      this.onSort(sortEvent);
      return;
    }

    const direction: $sortingOrder.asc | $sortingOrder.desc | undefined =
      sortEvent.sortDirection in tableSortDirectionMap ? tableSortDirectionMap[sortEvent.sortDirection] : undefined;
    this.onSort({
      column: sortEvent.columnKey,
      direction,
    });
  }

  public ngOnInit(): void {
    this.isDigitalAssetFindingsEnabled$ = this.featureFlagService
      .variation$(FeatureFlagCollection.digitalAssetFindings, false)
      .pipe(map(Boolean), AngularUtility.shareRef());

    this.tableConfig$ = this.isDigitalAssetFindingsEnabled$.pipe(
      map((isDigitalAssetFindingsEnabled: boolean): ITableConfig => {
        return this.getTableConfig(isDigitalAssetFindingsEnabled);
      }),
    );

    this.subscriptions.add(
      this.isDigitalAssetFindingsEnabled$.subscribe((isDigitalAssetFindingsEnabled: boolean): void => {
        this.isDigitalAssetFindingsEnabled = isDigitalAssetFindingsEnabled;
        this.dsTableConfig = this.buildDsTableConfig(isDigitalAssetFindingsEnabled);
        if (SharedCommonUtility.notNullishOrEmpty(this.scanIssues)) {
          this.updateTableData();
        }
      }),
    );

    this.selectedIssueKeys$ = this.selectionChanged$.pipe(
      startWith(null),
      map((): string[] => Array.from(this.selectedIssuesMap.keys())),
    );

    this.subscriptions.add(
      this.selectedIssueKeys$.subscribe((selectedIssueKeys: string[]): void => {
        this.selectedIssueKeySet = new Set<string>(selectedIssueKeys);
      }),
    );

    this.subscribeToDigitalPropertySwitch();

    this.subscriptions.add(
      this.activatedRoute.paramMap.subscribe((paramMap: ParamMap): void => {
        this.scanUrl = this.activatedRoute.snapshot.queryParamMap.get(ApiQueryOption.url);
        this.componentId = paramMap.get(Api.componentId);
        this.requestScanId = paramMap.get(Api.scanId);
        this.scanId = this.requestScanId;
        this.ruleId = paramMap.get(Api.ruleId);
        this.ruleDescription = this.activatedRoute.snapshot.queryParamMap.get(ApiQueryOption.ruleDescription);
        this.successCriteriaIdentifier = this.activatedRoute.snapshot.queryParamMap.getAll(ApiQueryOption.identifier);
        this.severity = $severity[this.activatedRoute.snapshot.queryParamMap.get(ApiQueryOption.severity)];
        this.totalIssues = Number(this.activatedRoute.snapshot.queryParamMap.get(ApiQueryOption.totalFinding));
        this.auditType = AuditTypes[paramMap.get(Api.auditType)];
        this.toolName = AuditTypeToAuditToolName[this.auditType];
        this.scanPointId = paramMap.get(Api.pageId);

        this.scanRule = {
          [$scanIssues.toolName]: this.toolName,
          [$scanIssues.severity]: this.severity,
          [$scanIssues.whyItMatters]: '',
          [$scanIssues.whatToDo]: '',
          [$scanIssues.description]: '',
          [$scanIssues.ruleId]: this.ruleId,
          [$scanIssues.title]: this.ruleDescription,
          [$scanIssues.successCriteria]: this.successCriteriaIdentifier.map((identifier: string): IScanIssuesSuccessCriteria => {
            return {
              [$auditStandardSuccessCriteria.identifier]: identifier,
              [$auditStandardSuccessCriteria.auditStandards]: [
                {
                  [$auditStandard.standard]: AuditDbStandard.wcag,
                },
              ],
            } as unknown as IScanIssuesSuccessCriteria;
          }),
          [$scanIssues.issues]: [],
          [$scanIssues.totalIssues]: this.totalIssues,
        };

        this.isAggregatedView = SharedCommonUtility.isNullish(this.scanUrl);

        this.scanIssues = null;
        this.getAuditReport();
      }),
    );

    this.subscriptions.add(this.activatedRoute.queryParams.subscribe(this.onQueryParamsChange.bind(this)));

    this.allPageSelected$ = this.selectionChanged$.pipe(
      switchMap((): Observable<boolean> => {
        const issueIds: string[] = this.getPageIssues().map((issue: IAutomatedScanIssue): string => issue.rawFindingId);
        const allSelected: boolean =
          issueIds.length > 0 && issueIds.every((id: string): boolean => this.selectedIssuesMap.has(id));
        return of(allSelected);
      }),
    );
  }

  public ngOnDestroy(): void {
    this.getAuditReportDetailsSubscription.unsubscribe();
    this.subscriptions.unsubscribe();
  }
}
